/**
 * Los links PROPIOS que el productor configura en el menú de su catálogo de
 * genética (`configuracion_micrositio.menu_links`).
 *
 * Vive acá y no dentro de cada página porque las DOS pantallas del catálogo
 * —la portada (`CatalogoPage`) y la ficha del animal (`AnimalCatalogoPage`)—
 * dibujan el mismo menú, y cada una tenía su propia copia casi idéntica de
 * este armado. El problema de tener dos copias no es la duplicación en sí: es
 * que el día que hay que endurecer algo —como el esquema de la URL, acá
 * abajo— se endurece UNA y la otra pantalla queda expuesta.
 */

import { urlSegura } from './urlSegura';

/**
 * Arma los items de `navPropia` para los links del productor.
 *
 * @param {object}   config   La `configuracion_micrositio` del catálogo.
 * @param {function} onAncla  Qué hacer con un `#ancla`, que es lo único que
 *                            cambia entre las dos pantallas: la portada baja
 *                            en la misma página, y la ficha navega a la
 *                            portada con el hash puesto —esas secciones viven
 *                            allá, y un `scrollIntoView` desde la ficha busca
 *                            un elemento que en esa página no existe: no
 *                            rompe, no hace nada, que es peor. Recibe el hash
 *                            COMPLETO, con el `#`.
 * @returns {Array}  Items para `SiteHeader` / `SiteFooter`.
 */
export const linksDelProductor = (config, onAncla) =>
  (config.menu_links || [])
    // Un link sin texto o sin destino no se dibuja: media fila cargada es una
    // fila que no lleva a ningún lado.
    .filter((l) => (l.label || '').trim() && (l.url || '').trim())
    .map((link, i) => ({
      key: `link-${i}`,
      label: link.label,
      onClick: () => {
        const url = link.url.trim();
        if (url.startsWith('#')) {
          onAncla(url);
          return;
        }
        const destino = urlSegura(url);
        // Un destino que no pasa la lista blanca no navega a ningún lado. Es
        // mejor un link que no hace nada que uno que ejecuta código.
        if (!destino) return;
        if (link.nueva_pestana) {
          // `noopener` NO es opcional: sin él, la pestaña que se abre puede
          // tocar `window.opener` y redirigir ESTA página a donde quiera.
          window.open(destino, '_blank', 'noopener,noreferrer');
        } else {
          // `_top` y no `window.location.href`: este catálogo está PENSADO
          // para vivir incrustado en el sitio que la cabaña ya tiene, y
          // `location.href` navega el IFRAME — un "Volver a nuestro sitio"
          // abriría el sitio entero adentro del marquito. Sin incrustar,
          // `_top` ES esta misma ventana, así que se comporta igual que antes.
          //
          // Y acá NO va `noopener`, a diferencia de la rama de arriba: con esa
          // feature activada el navegador IGNORA el nombre del target y lo
          // trata como `_blank`, que es justo lo contrario de lo que se pide.
          // Tampoco hace falta: `_top` no abre una ventana nueva, así que no
          // hay ningún `window.opener` que proteger.
          window.open(destino, '_top');
        }
      },
    }));

/**
 * Los items de menú de las SECCIONES del catálogo, con sus animales colgando.
 *
 * Esto tambien vive acá, y no llegó en el primer viaje. Cuando se extrajo este
 * módulo se trajeron sólo los links del productor y las secciones quedaron
 * copiadas en las dos pantallas — con el problema que este archivo existe para
 * evitar escrito en su propio docstring. Y ya habían divergido: en la ficha
 * cada animal del submenú navegaba a SU ficha, y en la portada los seis
 * bajaban todos al ancla de la sección. Mismo menú, mismos nombres de toro,
 * dos comportamientos según en qué página estuviera parado el comprador. Un
 * item que dice "PAYESERO" y no lleva a PAYESERO es el link muerto que este
 * mismo archivo dice que hay que sacar.
 *
 * Cada pantalla normaliza SU forma de datos a `{clave, titulo, animales}` antes
 * de llamar: la ficha ya la recibe asi del backend (`menu`), la portada la arma
 * de `data[clave].destacados`. Lo unico que se comparte es lo que diverge.
 *
 * @param {Array}    secciones  `[{clave, titulo, animales: [{slug, nombre}]}]`
 * @param {function} irAFicha   Recibe el slug del animal. Lo pasa cada pantalla
 *                              porque la portada rutea y la ficha tambien, pero
 *                              el dia que una quiera otra cosa no arrastra a la
 *                              otra.
 * @returns {Array}  Items para `navPropia`.
 */
export const seccionesDelCatalogo = (secciones, irAFicha) =>
  (secciones || []).map((sec) => ({
    key: sec.clave,
    label: sec.titulo,
    // El submenú lleva SOLO los animales. La primera versión abría con un
    // "Ver <sección>" que repetía el nombre del padre y bajaba al mismo lugar
    // que tocar el padre: una fila que no agrega nada y corre a los animales un
    // renglón para abajo.
    items: (sec.animales || []).map((a) => ({
      key: `${sec.clave}-${a.slug}`,
      label: a.nombre,
      onClick: () => irAFicha(a.slug),
    })),
  }));
